import { LoaderIcon } from "@/assets/icons";
import BigDataChart from "@/components/ui/chart/BigDataChart";
import DonutChart from "@/components/ui/chart/DonutChart";
import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import LineChart from "@/components/ui/chart/LineChart";
import {
  buildSeriesFromData,
  type ChartSeries
} from "@/components/ui/chart/utils";
import VerticalBarChart from "@/components/ui/chart/VerticalBarChart";
import { GROUPED_CHART_COLORS } from "@/constants/colors";
import { MESSAGE } from "@/constants/message";
import {
  AggregationType,
  WidgetAnalysisConfigResult,
  WidgetPreviewData,
  WidgetType
} from "@/types/widget";

import NoDataWidgetContent from "./NoDataWidgetContent";

interface WidgetConfigurationPreviewProps {
  widgetType: WidgetType;
  data: WidgetPreviewData | undefined;
  isLoading: boolean;
  chartHeight?: number;
  isDatasetPublished: boolean;
  /** Display name of the measure column — used as the LineChart legend label. */
  measureLabel?: string;
  /** Aggregation appended to the legend label for single-series charts. */
  aggregation?: string;
}

const WidgetConfigurationPreview = ({
  widgetType,
  data,
  isLoading,
  chartHeight,
  isDatasetPublished,
  measureLabel,
  aggregation
}: WidgetConfigurationPreviewProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <LoaderIcon className="animate-spin w-10 h-10 text-gray-500" />
        <p className="text-txtPlaceholder font-semibold">
          Mapping data to visualization...
        </p>
      </div>
    );
  }

  if (!isDatasetPublished) {
    return (
      <NoDataWidgetContent
        title={MESSAGE.WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_TITLE}
        description={MESSAGE.WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_DESCRIPTION}
      />
    );
  }

  if (!data) {
    return <NoDataWidgetContent />;
  }

  if (data.length === 0) {
    return (
      <NoDataWidgetContent
        title={MESSAGE.WIDGET_NO_DATA_PREVIEW_TITLE}
        description={MESSAGE.WIDGET_CARD_NO_DATA_AVAILABLE}
      />
    );
  }

  if (widgetType === WidgetType.BIG_NUMBER) {
    const item = data?.[0] as
      | { type: AggregationType; value: number; measure: string }
      | undefined;
    if (!item) return <NoDataWidgetContent />;
    return (
      <BigDataChart
        value={Number(item.value)}
        type={item.type}
        measure={item.measure}
      />
    );
  }

  // Rows format has no "label" key; AnalysisDataPoint format has "label"
  const firstRow = data[0] as Record<string, string | number>;
  const isRowsFormat = firstRow && !("label" in firstRow);

  const rowsData = isRowsFormat
    ? (data as Record<string, string | number>[])
    : null;
  const categoryKey = rowsData
    ? (Object.keys(firstRow).find((k) => typeof firstRow[k] === "string") ??
      "name")
    : undefined;
  const rowsSeries =
    rowsData && categoryKey ? buildSeriesFromData(rowsData, categoryKey) : null;

  const mappedData = (data as WidgetAnalysisConfigResult["data"])?.map(
    (item) => ({ label: item.label, value: item.value })
  );

  // Single-measure series so the legend shows the measure name (matches LineChart).
  const measureSeries: ChartSeries[] | undefined = measureLabel
    ? [
        {
          dataKey: "value",
          color: GROUPED_CHART_COLORS[0],
          label: measureLabel
        }
      ]
    : undefined;

  if (widgetType === WidgetType.BAR_V) {
    if (rowsData && categoryKey && rowsSeries) {
      return (
        <VerticalBarChart
          data={rowsData}
          categoryKey={categoryKey}
          series={rowsSeries}
          height={chartHeight ? chartHeight - 60 : undefined}
          isResponsive={false}
        />
      );
    }

    const singleData =
      (data as WidgetAnalysisConfigResult["data"])?.map((item) => ({
        label: item.label,
        value: Number(item.value)
      })) ?? [];

    return (
      <VerticalBarChart
        data={singleData}
        categoryKey="label"
        series={measureSeries}
        aggregation={aggregation}
        height={chartHeight ? chartHeight - 40 : undefined}
      />
    );
  }

  switch (widgetType) {
    case WidgetType.BAR_H:
      if (rowsData && categoryKey && rowsSeries) {
        return (
          <HorizontalBarChart
            data={rowsData}
            categoryKey={categoryKey}
            series={rowsSeries}
            height={chartHeight ? chartHeight - 40 : undefined}
          />
        );
      }
      return (
        <HorizontalBarChart
          data={
            mappedData?.map((item) => ({
              name: item.label,
              value: Number(item.value)
            })) || []
          }
          series={measureSeries}
          aggregation={aggregation}
          height={chartHeight ? chartHeight - 40 : undefined}
        />
      );
    case WidgetType.DONUT:
      return (
        <DonutChart
          data={
            mappedData?.map((item) => ({
              name: item.label,
              value: Number(item.value)
            })) || []
          }
          height={chartHeight ? chartHeight - 40 : undefined}
        />
      );
    case WidgetType.LINE:
      if (rowsData && categoryKey && rowsSeries) {
        return (
          <LineChart
            data={rowsData}
            categoryKey={categoryKey}
            series={rowsSeries}
            height={chartHeight ? chartHeight - 40 : undefined}
          />
        );
      }
      return (
        <LineChart
          data={
            mappedData?.map((item) => ({
              name: item.label,
              value: Number(item.value)
            })) || []
          }
          series={measureSeries}
          aggregation={aggregation}
          height={chartHeight ? chartHeight - 40 : undefined}
        />
      );
    default:
      return <NoDataWidgetContent />;
  }
};

export default WidgetConfigurationPreview;
